import { API_BASE_URL } from "../config";
import { AvailableSourcesResponse } from "../types";

const apiUrl = `${API_BASE_URL}/api`;

/**
 * Fetch every storage source the signed-in user can pick from.
 *
 * Returns the user's personal app folder (when it exists) plus any
 * tenant-approved shared folders or SharePoint sites the user can
 * personally access. The backend silently omits sources the user
 * cannot see in Microsoft 365. Incomplete results carry a flag so the UI
 * can offer retry while keeping successfully loaded sources usable.
 */
export const listAvailableSourceFolders = async (
  accessToken: string
): Promise<AvailableSourcesResponse> => {
  const url = `${apiUrl}/files/available-source-folders`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  }).catch(() => {
    throw new Error("Could not load storage sources. Retry.");
  });

  if (!response.ok) {
    throw new Error("Could not load storage sources. Retry.");
  }

  return response.json().catch(() => {
    throw new Error("Could not load storage sources. Retry.");
  });
};

/**
 * Fetch the document libraries inside a tenant-approved SharePoint site.
 *
 * The backend only returns libraries the current user can personally see.
 * A 404 response means the site source no longer exists (revoked, deleted,
 * or invisible to the user) and should be handled gracefully by the caller.
 */
export const listSiteLibraries = async (
  accessToken: string,
  sourceId: string
): Promise<AvailableSourcesResponse> => {
  const url = `${apiUrl}/files/site-libraries?source_id=${encodeURIComponent(sourceId)}`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ detail: "Unable to load site libraries." }));
    throw new Error(errorData.detail || "Unable to load site libraries.");
  }

  return response.json();
};
